import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Loading from '../../components/common/Loading';

interface AdminStats {
  totalUsuarios: number;
  usuariosAtivos: number;
  totalAnuncios: number;
  anunciosAtivos: number;
  totalDenuncias: number;
  denunciasPendentes: number;
  receitaMensal: number;
  receitaTotal: number;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Mock - em produção viria da API
    setTimeout(() => {
      setStats({
        totalUsuarios: 5432,
        usuariosAtivos: 3210,
        totalAnuncios: 12543,
        anunciosAtivos: 8765,
        totalDenuncias: 87,
        denunciasPendentes: 12,
        receitaMensal: 45670,
        receitaTotal: 567890
      });
      setLoading(false);
    }, 500);
  }, []);

  if (loading) return <Loading />;
  if (!stats) return null;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Admin Header */}
      <header className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <span className="text-2xl">🏛️</span>
              <h1 className="text-xl font-bold">BOILX Admin</h1>
            </div>
            <nav className="flex gap-6">
              <Link to="/admin" className="hover:text-green-400">Dashboard</Link>
              <Link to="/admin/usuarios" className="hover:text-green-400">Usuários</Link>
              <Link to="/admin/anuncios" className="hover:text-green-400">Anúncios</Link>
              <Link to="/admin/denuncias" className="hover:text-green-400">Denúncias</Link>
              <Link to="/admin/pagamentos" className="hover:text-green-400">Pagamentos</Link>
              <Link to="/" className="text-gray-400 hover:text-white">← Voltar ao Site</Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Painel Administrativo
        </h2>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            title="Usuários" 
            value={stats.totalUsuarios.toLocaleString()} 
            subtitle={`${stats.usuariosAtivos.toLocaleString()} ativos`}
            icon="👥"
            color="bg-blue-500"
          />
          <StatCard 
            title="Anúncios" 
            value={stats.totalAnuncios.toLocaleString()} 
            subtitle={`${stats.anunciosAtivos.toLocaleString()} ativos`}
            icon="📋"
            color="bg-green-500"
          />
          <StatCard 
            title="Denúncias" 
            value={stats.totalDenuncias.toString()} 
            subtitle={`${stats.denunciasPendentes} pendentes`}
            icon="⚠️"
            color="bg-yellow-500"
          />
          <StatCard 
            title="Receita Mensal" 
            value={`R$ ${stats.receitaMensal.toLocaleString()}`} 
            subtitle={`Total: R$ ${stats.receitaTotal.toLocaleString()}`}
            icon="💰"
            color="bg-purple-500"
          />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <QuickAction 
            to="/admin/usuarios" 
            icon="👥" 
            label="Gerenciar Usuários"
            description="Visualizar, bloquear, deletar"
          />
          <QuickAction 
            to="/admin/anuncios" 
            icon="📋" 
            label="Gerenciar Anúncios"
            description="Moderar, destacar, remover"
          />
          <QuickAction 
            to="/admin/denuncias" 
            icon="🚨" 
            label="Ver Denúncias"
            description={`${stats.denunciasPendentes} pendentes`}
          />
          <QuickAction 
            to="/admin/configuracoes" 
            icon="⚙️" 
            label="Configurações"
            description="Sistema e parâmetros"
          />
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Users */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Usuários Recentes
            </h3>
            <div className="space-y-3">
              {[
                { nome: 'João Silva', email: 'joao@email.com', tipo: 'VENDEDOR' },
                { nome: 'Maria Santos', email: 'maria@email.com', tipo: 'COMPRADOR' },
                { nome: 'Pedro Oliveira', email: 'pedro@email.com', tipo: 'PROFISSIONAL' },
              ].map((user, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      {user.nome[0]}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{user.nome}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    user.tipo === 'PROFISSIONAL' ? 'bg-purple-100 text-purple-700' :
                    user.tipo === 'VENDEDOR' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {user.tipo}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Reports */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Denúncias Pendentes
            </h3>
            <div className="space-y-3">
              {[
                { motivo: 'Spam', anuncio: 'Cavalo Mangalarga', status: 'PENDENTE' },
                { motivo: 'Informação falsa', anuncio: 'Touro Nelore', status: 'INVESTIGANDO' },
                { motivo: 'Fraude', anuncio: 'Sêmen importado', status: 'PENDENTE' },
              ].map((report, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{report.motivo}</p>
                    <p className="text-sm text-gray-500">{report.anuncio}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    report.status === 'PENDENTE' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {report.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ title, value, subtitle, icon, color }: {
  title: string;
  value: string;
  subtitle: string;
  icon: string;
  color: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center text-white text-2xl`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-xs text-gray-400">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

function QuickAction({ to, icon, label, description }: {
  to: string;
  icon: string;
  label: string;
  description: string;
}) {
  return (
    <Link 
      to={to}
      className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow"
    >
      <span className="text-3xl">{icon}</span>
      <h4 className="font-semibold text-gray-900 mt-2">{label}</h4>
      <p className="text-sm text-gray-500">{description}</p>
    </Link>
  );
}
